import React from "react";
import { Button, ButtonProps } from "@mui/material";
import { motion } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";
import { BRAND_COLOR, BRAND_HOVER } from "@kivunova/kivufrontendcommon";

export type BrandButtonVariant = "primary" | "outlined" | "error";

const baseSx = {
  borderRadius: 2,
  textTransform: "none" as const,
  fontWeight: 600,
  letterSpacing: "0.02em",
  px: 2,
  py: 1
};

const variantStyles: Record<BrandButtonVariant, ButtonProps["sx"]> = {
  primary: {
    ...baseSx,
    backgroundColor: BRAND_COLOR,
    color: "white",
    boxShadow: `0 2px 8px ${BRAND_COLOR}40`,
    "&:hover": {
      backgroundColor: BRAND_HOVER,
      boxShadow: `0 4px 12px ${BRAND_COLOR}50`
    }
  },
  outlined: {
    ...baseSx,
    backgroundColor: "white",
    color: BRAND_COLOR,
    border: `1px solid ${BRAND_COLOR}`,
    "&:hover": {
      backgroundColor: BRAND_HOVER,
      color: "white",
      borderColor: BRAND_HOVER
    }
  },
  error: {
    ...baseSx,
    backgroundColor: "white",
    color: "error.main",
    border: "1px solid",
    borderColor: "error.main",
    "&:hover": {
      backgroundColor: "error.main",
      color: "white"
    }
  }
};

export interface BrandButtonProps extends Omit<ButtonProps, "variant"> {
  variant?: BrandButtonVariant;
  /** Renders as a real router `<a href>` (open in new tab, copy link work). */
  to?: string;
}

const BrandButton: React.FC<BrandButtonProps> = ({
  variant = "primary",
  sx,
  children,
  to,
  ...props
}) => {
  if (to) {
    return (
      <Button
        component={RouterLink}
        to={to}
        variant={variant === "primary" ? "contained" : "outlined"}
        disableElevation
        sx={{ ...variantStyles[variant], ...sx }}
        {...props}
      >
        {children}
      </Button>
    );
  }

  return (
    <Button
      component={motion.button}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      variant={variant === "primary" ? "contained" : "outlined"}
      disableElevation
      sx={{ ...variantStyles[variant], ...sx }}
      {...props}
    >
      {children}
    </Button>
  );
};

export default BrandButton;
